import React, { useEffect, useRef } from 'react'
import { InputGroup, InputGroupText } from 'reactstrap'
import { InputGroupAddon } from '@src/components/reactstrap-compat'
import DateRangePicker from 'react-bootstrap-daterangepicker'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { XIcon } from '@src/assets/icons'
import Proptypes from 'prop-types'
import classnames from "classnames"
import _ from 'lodash'
import moment from 'moment'
//************************************//
import { dateRanges } from "../../assets/data/constants/dateRanges"
import { trans, isRtl, ensureStartOfDay, ensureEndOfDay } from '@utils'
//************************************//
import 'bootstrap-daterangepicker/daterangepicker.css'
import '@src/assets/scss/daterangepicker.scss'
//************************************//
const UncontrolledDateRange = React.forwardRef(({
  value = '',
  ranges = [],
  className = '',
  placeholder = '',
  size = 'md',
  format = 'L',
  displayFormat = 'L',
  minDate = null,
  maxDate = null,
  parentEl = 'body',
  linkedCalendars = true,
  showCustomRangeLabel = true,
  allowClear = false,
  cancelLabel = 'gen.actions.cancel',
  applyLabel = 'gen.actions.apply',
  customRangeLabel = 'gen.dateRange.custom',
  onChange = () => { },
  onInputChange,
  onClear,
  refs
}, currentRef) => {
  const ref = useRef(null)
  const Component = allowClear ? InputGroup : "div"
  const computedValue = _.size(value) > 0
    ? _.map(value, date => moment.utc(date).local())
    : undefined
  //************************************//
  const formatDateRange = (ranges) => {
    return _.mapValues(_.keyBy(ranges, item => trans(item.localeKey)), (range) => {
      return [range?.from, range?.to]
    })
  }
  //************************************//
  const formattedRanges = _.size(ranges) > 0
    ? formatDateRange(dateRanges(ranges))
    : formatDateRange(dateRanges(['today', 'yesterday', 'last7Days']))
  //************************************//
  const clearDate = () => {
    ref.current.$picker.val('')
    // onChange
    if (onChange) onChange([])
    if (onInputChange) onInputChange([])
    // onClear
    if (onClear) onClear({ ref: ref.current.$picker, value: null })
  }
  const handleApply = (event, picker) => {
    const startDate = picker.startDate
    const endDate = picker.endDate
    if (startDate || endDate) {
      const result = []
      if (startDate) result.push(ensureStartOfDay(startDate))
      if (endDate) result.push(ensureEndOfDay(endDate))
      if (onChange) onChange(result)
      if (onInputChange) onInputChange(result)
    }
  }
  const getLocaleDateFormat = (fmt) => {
    return moment.localeData().longDateFormat(fmt)
  }
  //************************************//
  useEffect(() => {
    if (refs) refs(ref)
  }, [ref])
  useEffect(() => {
    if (value !== undefined) {
      if (value) {
        const outputFormat = displayFormat || format
        const formattedValue = _.map(value, date => {
          return moment.utc(date).local().format(getLocaleDateFormat(outputFormat))
        })
        ref.current.$picker.val(formattedValue.join(' - '))
      }
    }
  }, [value, minDate, maxDate, displayFormat, format])
  //************************************//
  return (
    <Component>
      <DateRangePicker
        key={`${computedValue} ${minDate} ${maxDate}`}
        ref={ref}
        initialSettings={{
          autoUpdateInput: false,
          showDropdowns: true,
          opens: isRtl() ? 'left' : 'right',
          minYear: 2022,
          ranges: formattedRanges,
          parentEl,
          linkedCalendars,
          showCustomRangeLabel,
          startDate: computedValue?.length > 0 && !_.isNil(computedValue[0]) ? computedValue[0] : undefined,
          endDate: computedValue?.length > 0 && !_.isNil(computedValue[1]) ? computedValue[1] : undefined,
          minDate: minDate || moment('2022-01-01').utc(true).toISOString(),
          maxDate: !_.isNil(maxDate) ? maxDate : undefined,
          locale: {
            format: getLocaleDateFormat(format),
            cancelLabel: trans(cancelLabel),
            applyLabel: trans(applyLabel),
            customRangeLabel: trans(customRangeLabel)
          }
        }}
        onApply={handleApply}
      >
        <input type="text"
          className={classnames(className, { 'form-control bg-white': true, [`form-control-${size}`]: size })}
          placeholder={placeholder || trans('gen.dateRange.placeholder')}
          readOnly={true}
          style={{ opacity: 1 }}
        />
      </DateRangePicker>
      {allowClear && (
        <InputGroupAddon addonType='append'>
          <InputGroupText className="cursor-pointer" onClick={clearDate}>
            <FontAwesomeIcon icon={XIcon} />
          </InputGroupText>
        </InputGroupAddon>
      )}
    </Component>
  )
})
//************************************//
UncontrolledDateRange.propTypes = {
  value: Proptypes.array,
  ranges: Proptypes.array,
  className: Proptypes.string,
  placeholder: Proptypes.string,
  size: Proptypes.string,
  format: Proptypes.string,
  displayFormat: Proptypes.string,
  minDate: Proptypes.string,
  maxDate: Proptypes.string,
  parentEl: Proptypes.string,
  linkedCalendars: Proptypes.bool,
  showCustomRangeLabel: Proptypes.bool,
  allowClear: Proptypes.bool,
  cancelLabel: Proptypes.string,
  applyLabel: Proptypes.string,
  customRangeLabel: Proptypes.string,
  onChange: Proptypes.func,
  onClear: Proptypes.func
}
//************************************//
export default UncontrolledDateRange
